import L from 'leaflet';
import {
    CELL_WIDTH, CELL_HEIGHT, GRID_PADDING,
    PLACEMENT_PARAMS_BY_SIZE, DEFAULT_ICON_SCALE,
    MARKER_TRANSFORM_RULES,
    BASE_MODULE_SLOT_WIDTH, BASE_MODULE_SLOT_HEIGHT,
    DEFAULT_MODULE_DIMENSIONS,
} from './mapConstants.js';
import { generateSvgString, getCategoryAndSubcategory, initializeIconsStructure } from './mapIconUtils.js';
import { GAME_MODES } from './mapMain.js';

const ICE_CAVE_LAYOUT = [
  { moduleName: 'IceCave_Path', row: 4, col: 0 }, { moduleName: 'IceCave_Spiral', row: 4, col: 1 },
  { moduleName: 'IceCave_Maze', row: 4, col: 2 }, { moduleName: 'IceCave_FourRoute', row: 4, col: 3 },
  { moduleName: 'IceCave_Altar', row: 4, col: 4 }, { moduleName: 'IceCave_Bridge', row: 3, col: 0 },
  { moduleName: 'IceCave_CavePathway', row: 3, col: 1 }, { moduleName: 'IceCave_Guardpost', row: 3, col: 2 },
  { moduleName: 'IceCave_Pyramid', row: 3, col: 3 }, { moduleName: 'IceCave_DualCaverns', row: 3, col: 4 },
  { moduleName: 'IceCave_WolfCave', row: 2, col: 0 }, { moduleName: 'IceCave_CaveSwamp', row: 2, col: 1 },
  { moduleName: 'IceCave_IcicleCave', row: 2, col: 2 }, { moduleName: 'IceCave_Barricade', row: 2, col: 3 },
  { moduleName: 'IceCave_CrossRoad', row: 2, col: 4 }, { moduleName: 'IceCave_Sanctum', row: 1, col: 0 },
  { moduleName: 'IceCave_Hut_03', row: 1, col: 1 }, { moduleName: 'IceCave_Hive_03', row: 0, col: 2 },
  { moduleName: 'IceCave_NarrowBridge', row: 1, col: 3}, { moduleName: 'IceCave_Cabin_Raft', row: 1, col: 4 },
  { moduleName: 'IceCave_Quarry', row: 0, col: 0 }, { moduleName: 'IceCave_Hut_01', row: 0, col: 1 },
  { moduleName: 'IceCave_Hut_02', row: 0, col: 3 }, { moduleName: 'IceCave_Watchtower', row: 0, col: 4 },
];
const ICE_ABYSS_LAYOUT = [
  { moduleName: 'IceAbyss_ReversedPyramid', row: 0, col: 0 }, { moduleName: 'IceAbyss_DividingHill', row: 0, col: 1 },
  { moduleName: 'IceAbyss_ImpRitualRooms', row: 0, col: 2 }, { moduleName: 'IceAbyss_ShallowValley', row: 1, col: 0 },
  { moduleName: 'IceAbyss_WyvernLair', row: 1, col: 1 }, { moduleName: 'IceAbyss_StaircaseHill', row: 1, col: 2 },
  { moduleName: 'IceAbyss_CharnelHouse', row: 2, col: 0 }, { moduleName: 'IceAbyss_Pillars', row: 2, col: 1 },
  { moduleName: 'IceAbyss_DeathPit', row: 2, col: 2 },
];

const RARITY_MAP = {
    _0001: { name: 'Junk', color: '#5A524A' }, _1001: { name: 'Poor', color: '#888888' },
    _2001: { name: 'Common', color: '#EEEEEE' }, _3001: { name: 'Uncommon', color: '#80D600' },
    _4001: { name: 'Rare', color: '#00A1FF' }, _5001: { name: 'Epic', color: '#D067FF' },
    _6001: { name: 'Legend', color: '#FF9A00' }, _7001: { name: 'Unique', color: '#ECD99A' },
    _8001: { name: 'Artifact', color: '#f81b1a' }
};
const DEFAULT_RARITY = { name: 'Unknown', color: '#FFFFFF' };
const RARITY_ORDER = ['Junk', 'Poor', 'Common', 'Uncommon', 'Rare', 'Epic', 'Legend', 'Unique', 'Artifact'];
const getRarityRank = (rarityName) => { const rank = RARITY_ORDER.indexOf(rarityName); return rank === -1 ? RARITY_ORDER.length : rank; };

const LUCK_GRADE_RARITY_MAPPING = {
    0: RARITY_MAP['_0001'],
    1: RARITY_MAP['_1001'],
    2: RARITY_MAP['_2001'],
    3: RARITY_MAP['_3001'],
    4: RARITY_MAP['_4001'],
    5: RARITY_MAP['_5001'],
    6: RARITY_MAP['_6001'],
    7: RARITY_MAP['_7001'],
    8: RARITY_MAP['_8001']
};

function getItemRarity(itemId, luckGrade = null) {
    let numericLuckGrade = luckGrade;
    if (typeof luckGrade === 'string') {
        const parsed = parseInt(luckGrade, 10);
        if (!isNaN(parsed) && String(parsed) === luckGrade) {
            numericLuckGrade = parsed;
        }
    }

    const rarityFromLuckGrade = LUCK_GRADE_RARITY_MAPPING[numericLuckGrade];
    if (rarityFromLuckGrade) {
        return rarityFromLuckGrade;
    }

    return DEFAULT_RARITY;
}

function cleanItemIdForDisplay(itemId) {
     if (!itemId || typeof itemId !== 'string') return 'Unknown Item';
     let cleaned = itemId.replace(/^(Id_Item_|Id_Lootdrop_)/, '');
     cleaned = cleaned.replace(/_(\d{4})$/, '');
     return cleaned.replace(/_/g, ' ');
}
function cleanMonsterIdForDisplay(monsterId) {
    if (!monsterId || typeof monsterId !== 'string') return 'Unknown Object';
    return monsterId.replace(/^(Id_Monster_|Id_Props_|Id_)/, '').replace(/_/g, ' ');
}
function cleanModuleNameForDisplayFallback(moduleName) {
    if (!moduleName || typeof moduleName !== 'string') return 'Unnamed Module';
    return moduleName.replace(/_/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2');
}

export function inferEntityGroupId(originalObjectName) {
    if (!originalObjectName || typeof originalObjectName !== 'string') return null;
    if (originalObjectName.startsWith('Id_Monster_')) return originalObjectName;
    if (originalObjectName.startsWith('Id_Props_')) return originalObjectName;
    return null;
}
function formatDisplayName(name, maxLength = 400) {
    if (!name || typeof name !== 'string' || name.length === 0) return name;
    let formattedName = name.replace(/_/g, ' ');
    formattedName = formattedName.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
    formattedName = formattedName.trim();
    if (formattedName.length > maxLength) {
        formattedName = formattedName.substring(0, maxLength) + '…';
    }
    return formattedName;
}

function generateHtmlForDropCheckPool(poolData, useRarityColor = true, isInitiallyExpanded = false) {
    if (!poolData || !poolData.displayableItems || poolData.displayableItems.length === 0) return '';

    const sortedItemsInPool = poolData.displayableItems.slice().sort((a, b) => {
        const rarityA = getItemRarity(a?.itemId, a?.luckGrade);
        const rarityB = getItemRarity(b?.itemId, b?.luckGrade);
        const rankA = getRarityRank(rarityA.name); const rankB = getRarityRank(rarityB.name);
        if (rankA !== rankB) return rankB - rankA;

        const chanceA = a.finalCalculatedChance ?? 0;
        const chanceB = b.finalCalculatedChance ?? 0;
        if (chanceB !== chanceA) return chanceB - chanceA;

        const nameA_check = a?.artifactName || a?.itemName || cleanItemIdForDisplay(a?.itemId) || 'Unknown';
        const nameB_check = b?.artifactName || b?.itemName || cleanItemIdForDisplay(b?.itemId) || 'Unknown';
        return nameA_check.localeCompare(nameB_check);
    });


    let poolTitle = poolData.checkType ? `${poolData.checkType}` : `Loot Pool ${poolData.checkIndex + 1}`;
    poolTitle = formatDisplayName(poolTitle);

    let chanceHtml = '';
    if (typeof poolData.chance_check_yields_any_item_N_rolls === 'number' && poolData.chance_check_yields_any_item_N_rolls >= 0) {
        chanceHtml = `<p style="font-size:0.8em; margin-bottom:3px; text-align: left;">(${poolData.N_rolls_for_this_check} roll${poolData.N_rolls_for_this_check !== 1 ? 's' : ''}): <b>${poolData.chance_check_yields_any_item_N_rolls.toFixed(2)}%</b> chance for any item from pool</p>`;
    }

    let tableContentHtml = `<div class="pool-items-scroll-container"><table class="loot-table"><thead><tr><th>Item (Count)</th><th style="text-align: right; white-space: nowrap;">Drop Chance</th></tr></thead><tbody>`;

    sortedItemsInPool.forEach(drop => {
        let displayName = drop.itemName || cleanItemIdForDisplay(drop.itemId);
        if (drop.itemId && drop.itemId.endsWith('_8001') && drop.artifactName) displayName = drop.artifactName;

        const dropChancePercentage = (drop.finalCalculatedChance ?? 0) * 100;

        const itemRarityToUse = getItemRarity(drop.itemId, drop.luckGrade);
        const itemColor = useRarityColor ? itemRarityToUse.color : DEFAULT_RARITY.color;
        const itemCount = drop.itemCount > 1 ? ` (x${drop.itemCount})` : '';

        tableContentHtml += `<tr><td style="color: ${itemColor};">${escapeHTML(displayName)}${itemCount}</td><td style="text-align: right; white-space: nowrap; color: ${itemColor};">${dropChancePercentage.toFixed(2)}%</td></tr>`;
    });
    tableContentHtml += `</tbody></table></div>`;

    const icon = isInitiallyExpanded ? '▼' : '►';
    const contentDisplay = isInitiallyExpanded ? 'block' : 'none';
    return `<div class="drop-check-pool-container collapsible-pool"><div class="collapsible-trigger"><span class="pool-toggle-icon">${icon}</span><b class="pool-title-text">${escapeHTML(poolTitle)}</b></div><div class="pool-content" style="display: ${contentDisplay};">${chanceHtml}${tableContentHtml}</div></div>`;
}

function escapeHTML(text)  {
    if (typeof text !== 'string') return '';
    return text.replace(/[&<>"']/g, function (match) {
        return {
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#39;'
        }[match];
    });
}

export function extractRulesLogic(dataForGrade, entityId) {
    let rules = null;
    if (dataForGrade && typeof dataForGrade === 'object') {
        if (entityId && dataForGrade[entityId]) {
            rules = dataForGrade[entityId];
        } else if (typeof dataForGrade.percentage === 'number' && Array.isArray(dataForGrade.grades_allowed)) {
            rules = dataForGrade;
        } else {
            const keysInGradeData = Object.keys(dataForGrade);
            if (keysInGradeData.length === 1) {
                const singleKey = keysInGradeData[0];
                const potentialRulesObject = dataForGrade[singleKey];
                if (potentialRulesObject && typeof potentialRulesObject.percentage === 'number' && Array.isArray(potentialRulesObject.grades_allowed)) {
                    rules = potentialRulesObject;
                } else {
                    rules = dataForGrade;
                }
            } else {
                rules = dataForGrade;
            }
        }
    }
    return rules;
}

export function createMarkerObject(moduleInfo, actorData, index, selectedMapType, gameMode, mapIconScale, localizedStringParam) {
    const { object_name: originalObjectName, object_location, type: objectType, spawn_chances_by_grade, associated_lootdrop_ids } = actorData;
    const { X, Y, Z } = object_location || {};

    const { gridBounds, dimensions: gridLayoutDimensions, moduleName: currentModuleName, moduleDisplayName: currentModuleDisplayName, originalMetaDimensions, moduleTextureActualDimensions } = moduleInfo;


    if (X === undefined || Y === undefined || isNaN(X) || isNaN(Y)) {
        return null;
    }
    if (!gridBounds || !Array.isArray(gridBounds[0]) || !originalObjectName) {
        return null;
    }

    const tdForPlacementParams = gridLayoutDimensions || DEFAULT_MODULE_DIMENSIONS;

    let actorCoordSystemWidth;
    let actorCoordSystemHeight;

    const transformRule = MARKER_TRANSFORM_RULES[selectedMapType]?.[currentModuleName];

    if (transformRule && typeof transformRule.originalActorCoordWidth === 'number' && transformRule.originalActorCoordWidth > 0 &&
        typeof transformRule.originalActorCoordHeight === 'number' && transformRule.originalActorCoordHeight > 0) {
        actorCoordSystemWidth = transformRule.originalActorCoordWidth;
        actorCoordSystemHeight = transformRule.originalActorCoordHeight;
    } else if (moduleTextureActualDimensions && typeof moduleTextureActualDimensions.width === 'number' && moduleTextureActualDimensions.width > 0 &&
               typeof moduleTextureActualDimensions.height === 'number' && moduleTextureActualDimensions.height > 0) {
        actorCoordSystemWidth = moduleTextureActualDimensions.width;
        actorCoordSystemHeight = moduleTextureActualDimensions.height;
    } else if (originalMetaDimensions && typeof originalMetaDimensions.width === 'number' && originalMetaDimensions.width > 0 &&
               typeof originalMetaDimensions.height === 'number' && originalMetaDimensions.height > 0) {
        actorCoordSystemWidth = originalMetaDimensions.width;
        actorCoordSystemHeight = originalMetaDimensions.height;
    } else {
        actorCoordSystemWidth = (gridLayoutDimensions?.width > 0 ? gridLayoutDimensions.width : BASE_MODULE_SLOT_WIDTH);
        actorCoordSystemHeight = (gridLayoutDimensions?.height > 0 ? gridLayoutDimensions.height : BASE_MODULE_SLOT_HEIGHT);
    }

    let currentActorX_transformed = X;
    let currentActorY_transformed = Y;
    let doFlipX = false, doFlipY = false, rotationAngle = 0, actorInternalOffsetX = 0, actorInternalOffsetY = 0;

    if (transformRule) {
        doFlipX = transformRule.flipX !== undefined ? transformRule.flipX : doFlipX;
        doFlipY = transformRule.flipY !== undefined ? transformRule.flipY : doFlipY;
        rotationAngle = transformRule.rotation !== undefined ? transformRule.rotation : rotationAngle;
        actorInternalOffsetX = transformRule.actorOffsetX !== undefined ? transformRule.actorOffsetX : actorInternalOffsetX;
        actorInternalOffsetY = transformRule.actorOffsetY !== undefined ? transformRule.actorOffsetY : actorInternalOffsetY;
    }

    if (doFlipX) currentActorX_transformed = actorCoordSystemWidth - currentActorX_transformed;
    if (doFlipY) currentActorY_transformed = actorCoordSystemHeight - currentActorY_transformed;

    if (rotationAngle !== 0) {
        const centerX = actorCoordSystemWidth / 2;
        const centerY = actorCoordSystemHeight / 2;
        let tempX = currentActorX_transformed - centerX;
        let tempY = currentActorY_transformed - centerY;

        const rad = rotationAngle * (Math.PI / 180);
        const cosA = Math.cos(rad);
        const sinA = Math.sin(rad);

        const rotatedX = tempX * cosA - tempY * sinA;
        const rotatedY = tempX * sinA + tempY * cosA;

        currentActorX_transformed = rotatedX + centerX;
        currentActorY_transformed = rotatedY + centerY;
    }

    currentActorX_transformed += actorInternalOffsetX;
    currentActorY_transformed += actorInternalOffsetY;

    let sizeKey = 'default';
    if (tdForPlacementParams?.width && tdForPlacementParams?.height) {
        const key = `${tdForPlacementParams.width}x${tdForPlacementParams.height}`;
        if (PLACEMENT_PARAMS_BY_SIZE[key]) {
            sizeKey = key;
        }
    }
    const params = PLACEMENT_PARAMS_BY_SIZE[sizeKey];

    const normalizedX = currentActorX_transformed / actorCoordSystemWidth;
    const normalizedY = currentActorY_transformed / actorCoordSystemHeight;

    const inputXForParams = normalizedX * (tdForPlacementParams.width || BASE_MODULE_SLOT_WIDTH);
    const inputYForParams = normalizedY * (tdForPlacementParams.height || BASE_MODULE_SLOT_HEIGHT);

    const displayX_Rel = (inputYForParams) * params.scaleY * 0.25;
    const displayY_Rel = (inputXForParams) * params.scaleX * 0.20;

    const finalLat_Rel = displayY_Rel + params.offsetY + 1300;
    const finalLng_Rel = displayX_Rel + params.offsetX + 1000;

    const [minLatTile, minLngTile] = gridBounds[0];
    const finalLat = minLatTile + finalLat_Rel;
    const finalLng = minLngTile + finalLng_Rel;

    if (isNaN(finalLat) || isNaN(finalLng)) {
        return null;
    }

    const markerId = `${selectedMapType}-${currentModuleName}-${gameMode}-${originalObjectName}-${index}`;
    const svgString = generateSvgString(originalObjectName, mapIconScale, objectType);

    const entityGroupIdToUse = actorData.entityGroupId || inferEntityGroupId(originalObjectName);

    

    let prefix = '10'; 
    const gameModeObject = Object.values(GAME_MODES).find(m => m.key === gameMode);
    if(gameModeObject) {
        switch (gameModeObject.key) {
            case GAME_MODES.ADVENTURE.key: prefix = '10'; break;
            case GAME_MODES.NORMAL.key: prefix = '20'; break;
            case GAME_MODES.HR.key: prefix = '30'; break;
        }
    }

    let suffix = '01'; 
    const mapIdLower = String(selectedMapType || '').toLowerCase();
    if (mapIdLower.includes('ruin') || mapIdLower.includes('goblin')) suffix = '21'; 
    else if (mapIdLower.includes('crypt') || mapIdLower.includes('ice cave')) suffix = '22'; 
    else if (mapIdLower.includes('inferno') || mapIdLower.includes('hell') || mapIdLower.includes('abyss')) suffix = '23'; 
    

    let baseGradeKey = prefix + suffix;
    if (baseGradeKey === "0000" || baseGradeKey === "0001" || baseGradeKey === "0021" ) baseGradeKey = "0"; 

    let finalSelectedGradeKey = baseGradeKey;
    let initiallySelectedSpawns = null;

    if (spawn_chances_by_grade && typeof spawn_chances_by_grade === 'object' && Object.keys(spawn_chances_by_grade).length > 0) {
        const availableKeys = Object.keys(spawn_chances_by_grade).sort();

        if (spawn_chances_by_grade[baseGradeKey]) finalSelectedGradeKey = baseGradeKey;
        else {
            const pStr = baseGradeKey.substring(0,2); const firstChar = baseGradeKey.charAt(0);
            const fullPKeys = availableKeys.filter(k => k.startsWith(pStr)); const firstCharKeys = availableKeys.filter(k => k.startsWith(firstChar));
            if (baseGradeKey === "0" && spawn_chances_by_grade['0']) finalSelectedGradeKey = '0';
            else if (fullPKeys.length > 0) finalSelectedGradeKey = fullPKeys[0];
            else if (firstCharKeys.length > 0) finalSelectedGradeKey = firstCharKeys[0];
            else if (spawn_chances_by_grade['0']) finalSelectedGradeKey = '0';
            else if (availableKeys.length > 0) finalSelectedGradeKey = availableKeys[0];
        }

        const primaryGradeData = spawn_chances_by_grade[finalSelectedGradeKey];
        let potentialSpawns = extractRulesLogic(primaryGradeData, entityGroupIdToUse);

        let primarySpawnIsEmptyObject = potentialSpawns && typeof potentialSpawns === 'object' && Object.keys(potentialSpawns).length === 0;
        let primaryHasGradesAllowed = potentialSpawns && Array.isArray(potentialSpawns.grades_allowed);

        if (!primaryHasGradesAllowed && primarySpawnIsEmptyObject &&
            finalSelectedGradeKey !== '0' && spawn_chances_by_grade['0']) {
            let fallbackGradeData = spawn_chances_by_grade['0'];
            let fallbackSpawns = extractRulesLogic(fallbackGradeData, entityGroupIdToUse);

            if (fallbackSpawns && Array.isArray(fallbackSpawns.grades_allowed)) {
                potentialSpawns = fallbackSpawns;
            } else if (fallbackSpawns && Object.keys(fallbackSpawns).length > 0 && primarySpawnIsEmptyObject) {
                potentialSpawns = fallbackSpawns;
            }
        }
        initiallySelectedSpawns = potentialSpawns;

        if (initiallySelectedSpawns === null && primaryGradeData && typeof primaryGradeData === 'object' && Object.keys(primaryGradeData).length === 0) {
            initiallySelectedSpawns = primaryGradeData;
        }
    }

    let finalSpawnChancesObject = initiallySelectedSpawns;
    if (actorData.allowed_pools && Array.isArray(actorData.allowed_pools) && actorData.allowed_pools.length > 0) {
        if (finalSpawnChancesObject && typeof finalSpawnChancesObject === 'object') {
            finalSpawnChancesObject.allowed_pools = actorData.allowed_pools;
        } else {
            finalSpawnChancesObject = { allowed_pools: actorData.allowed_pools };
            if (initiallySelectedSpawns && typeof initiallySelectedSpawns === 'object') {
                finalSpawnChancesObject = { ...initiallySelectedSpawns, allowed_pools: actorData.allowed_pools };
            } else if (initiallySelectedSpawns === null) {
                finalSpawnChancesObject = { ...{}, allowed_pools: actorData.allowed_pools};
            }
        }
    }

    const finalDisplayTitle = (localizedStringParam && localizedStringParam !== "N/A") ? localizedStringParam : cleanMonsterIdForDisplay(originalObjectName);

    return {
        id: markerId, moduleName: currentModuleName, moduleDisplayName: currentModuleDisplayName, object_name: originalObjectName, object_type: objectType,
        entityGroupId: entityGroupIdToUse,
        dungeonGradeKey: finalSelectedGradeKey,
        spawn_chances: finalSpawnChancesObject,
        associated_lootdrop_ids: associated_lootdrop_ids || [], gameMode, customIconSvg: svgString, position: [finalLat, finalLng],
        zIndexValue: Z ?? 0, interactive: true, LocalizedString: finalDisplayTitle,
    };
}
const POOL_CATEGORIES = {
    PRIMARY: { label: 'Key Equipment & Gear', order: 1 },
    SECONDARY: { label: 'Valuables & Crafting', order: 2 },
    COMMON: { label: 'Common & Utility', order: 3 },
    OTHER: { label: 'Other Drops', order: 4 }
};
function getPoolCategoryKey(checkType) {
    if (!checkType) return 'OTHER';
    const type = String(checkType).toLowerCase();
    if (type.includes('mainequipment') || type.includes('weapon') || type.includes('armor') || type.includes('artifact') || type.includes('trinket')) return 'PRIMARY';
    if (type.includes('gear')) return 'PRIMARY';
    if (type.includes('miscellaneous') || type.includes('crafting') || type.includes('relic') || type.includes('quest') || type.includes('gold chest') || type.includes('treasure')) return 'SECONDARY';
    if (type.includes('consumable') || type.includes('utility')) return 'COMMON';
    if (type.includes('generic drop') || type.includes('miscellaneous') || type.includes('misc') || (type.includes('eventcurrency')))  return 'COMMON';
    return 'OTHER';
}

function deriveSpecificId(name, context, lootDataForGrade) {
    if (!name || !lootDataForGrade) return null;
    const variations = [
        `ID_LootDropGroup_${name.replace(/ /g, "_")}`,
        `Id_LootDropGroup_${name.replace(/ /g, "_")}`,
        `ID_LootDropGroup_${name.replace(/ /g, "")}`,
        `Id_LootDropGroup_${name.replace(/ /g, "")}`
    ];
    for (const variation of variations) {
        if (lootDataForGrade[variation]) return variation;
    }
    return null;
}

function findLootGroupEntryCaseInsensitive(dataObject, keyToFind) {
    if (!dataObject || typeof keyToFind !== 'string') {
        return undefined;
    }
    const lowerKeyToFind = keyToFind.toLowerCase();
    if (Object.prototype.hasOwnProperty.call(dataObject, keyToFind)) {
        return dataObject[keyToFind];
    }
    for (const keyInObject in dataObject) {
        if (Object.prototype.hasOwnProperty.call(dataObject, keyInObject)) {
            if (keyInObject.toLowerCase() === lowerKeyToFind) {
                return dataObject[keyInObject];
            }
        }
    }
    return undefined;
}

export function generatePopupContentForMarker(
    displayObjectNameParam,
    lootDataForGrade,
    ZParam,
    moduleNameParam,
    spawnChancesDataParam,
    gameModeKeyParam,
    entityGroupIdFromMarkerParam,
    dungeonGradeKeyParam,
    associatedLootdropIdsFromMarkerParam,
    LocalizedStringForMarkerParam,
    searchTermParam,
    moduleDisplayNameParam
) {
    const LocalizedString = LocalizedStringForMarkerParam;
    const Z = ZParam;
    const effectiveGameModes = typeof GAME_MODES !== 'undefined' ? GAME_MODES : undefined;

    const zText = Z !== undefined && !isNaN(Z) ? `<div style="font-size: 0.8em;">H: ${Z.toFixed(0)}</div>` : '';
    const effectiveModName = (moduleDisplayNameParam && moduleDisplayNameParam !== "N/A") ? moduleDisplayNameParam : cleanModuleNameForDisplayFallback(moduleNameParam);
    const headerText = LocalizedString && LocalizedString !== "N/A" ? LocalizedString : cleanMonsterIdForDisplay(displayObjectNameParam);
    const mainHeaderHtml = `<div class="popup-main-header"><b>${escapeHTML(headerText)}</b><br/><span style="font-size: 0.9em; color: #AAA;">(@${escapeHTML(effectiveModName)})</span>${zText}</div>`;

    const lootDataOutput = { overallLootTitleHtml: '', tabs: [], noLootMessageHtmlIfAny: '' };
    let allParsableDropCheckObjects = [];
    let anyLootConfiguredOverall = false;
    let logicUsed = 'AllowedPools_Only_V3.1_FilterEmptyTabs';

    const maxAllowedRarityName = 'Artifact';
    const currentMaxAllowedRank = getRarityRank(maxAllowedRarityName);

    const poolsToIterate = spawnChancesDataParam?.allowed_pools;
    let rulesForLuckLookup = null;

    if (spawnChancesDataParam && typeof spawnChancesDataParam === 'object') {
        const potentialRulesObject = { ...spawnChancesDataParam };
        if (Object.prototype.hasOwnProperty.call(potentialRulesObject, 'allowed_pools')) {
            delete potentialRulesObject.allowed_pools;
        }
        if (Object.keys(potentialRulesObject).length > 0) {
            rulesForLuckLookup = potentialRulesObject;
        } else {
            rulesForLuckLookup = {};
        }
    } else {
        rulesForLuckLookup = {};
    }

    const canProcessAllowedPools = poolsToIterate && Array.isArray(poolsToIterate) && poolsToIterate.length > 0;

    if (canProcessAllowedPools && lootDataForGrade) {
        poolsToIterate.forEach(allowedPoolId_originalCase => {
            const lootGroupEntry = findLootGroupEntryCaseInsensitive(lootDataForGrade, allowedPoolId_originalCase);
            if (!lootGroupEntry || !lootGroupEntry.gradeSpecificLoot || !Array.isArray(lootGroupEntry.gradeSpecificLoot.dropChecks) || lootGroupEntry.gradeSpecificLoot.dropChecks.length === 0) {
                return;
            }
            const dropChecksForThisPool = lootGroupEntry.gradeSpecificLoot.dropChecks;
            dropChecksForThisPool.forEach((checkData, checkIndex) => {
                const explicitlyAllowedLuckGrades = new Set();
                let applyLuckGradeFilter = false;
                let luckConfigToUse = null;
                if (rulesForLuckLookup && typeof rulesForLuckLookup === 'object') {
                    if (checkData.lootDropId && rulesForLuckLookup[checkData.lootDropId]?.grades_allowed) {
                        luckConfigToUse = rulesForLuckLookup[checkData.lootDropId];
                    } else if (rulesForLuckLookup[allowedPoolId_originalCase]?.grades_allowed) {
                        luckConfigToUse = rulesForLuckLookup[allowedPoolId_originalCase];
                    } else {
                        const caseInsensitiveKey = Object.keys(rulesForLuckLookup).find(k => k.toLowerCase() === allowedPoolId_originalCase.toLowerCase());
                        if (caseInsensitiveKey && rulesForLuckLookup[caseInsensitiveKey]?.grades_allowed) {
                             luckConfigToUse = rulesForLuckLookup[caseInsensitiveKey];
                        } else if (Array.isArray(rulesForLuckLookup.grades_allowed) && typeof rulesForLuckLookup.percentage === 'number') {
                             luckConfigToUse = rulesForLuckLookup;
                        }
                    }
                }
                if (luckConfigToUse && Array.isArray(luckConfigToUse.grades_allowed)) {
                    if (luckConfigToUse.grades_allowed.length > 0) {
                        luckConfigToUse.grades_allowed.forEach(ga => {
                            if (typeof ga.LuckGrade === 'number' && typeof ga.DropRate === 'number' && ga.DropRate > 0) {
                                explicitlyAllowedLuckGrades.add(ga.LuckGrade);
                            }
                        });
                        applyLuckGradeFilter = true;
                    }
                }
                let N_for_this_check = (typeof checkData.lootDropCount === 'number' && checkData.lootDropCount > 0) ? checkData.lootDropCount : 1;
                if (!checkData.possibleDrops || !Array.isArray(checkData.possibleDrops) || checkData.possibleDrops.length === 0) {
                    return;
                }
                let filteredPossibleDrops_byLuck = checkData.possibleDrops;
                if (applyLuckGradeFilter) {
                    filteredPossibleDrops_byLuck = checkData.possibleDrops.filter(drop =>
                        typeof drop.luckGrade !== 'number' || explicitlyAllowedLuckGrades.has(drop.luckGrade)
                    );
                }
                if (filteredPossibleDrops_byLuck.length === 0 && checkData.possibleDrops.length > 0) {
                    anyLootConfiguredOverall = true;
                }
                if (filteredPossibleDrops_byLuck.length === 0) return;

                const displayableItemsForThisCheck = [];
                let P_any_item_one_roll_from_check = 0;
                const rarityGroups = new Map();
                filteredPossibleDrops_byLuck.forEach(drop => {
                    if (!drop.itemId) return;
                    const itemRarityObject = getItemRarity(drop.itemId, drop.luckGrade);
                    const itemRank = getRarityRank(itemRarityObject.name);
                    if (itemRank > currentMaxAllowedRank) return;
                    anyLootConfiguredOverall = true;
                    const rarityName = itemRarityObject.name;
                    if (!rarityGroups.has(rarityName)) {
                        rarityGroups.set(rarityName, { items: [], totalWeightInGroup: 0, groupChancePerRoll: undefined });
                    }
                    const group = rarityGroups.get(rarityName);
                    group.items.push(drop);
                    group.totalWeightInGroup += (drop.dropWeight ?? 0);
                    if (group.groupChancePerRoll === undefined && typeof drop.chancePerRoll === 'number') {
                        group.groupChancePerRoll = drop.chancePerRoll;
                    }
                });
                rarityGroups.forEach((groupData) => {
                    const { items, totalWeightInGroup, groupChancePerRoll } = groupData;
                    if (typeof groupChancePerRoll !== 'number' || groupChancePerRoll <= 0 || totalWeightInGroup === 0) {
                        items.forEach(drop => {
                            displayableItemsForThisCheck.push({
                                itemName: drop.itemName, itemId: drop.itemId, artifactName: drop.artifactName,
                                itemCount: drop.itemCount || 1, finalCalculatedChance: 0,
                                rarityName: getItemRarity(drop.itemId, drop.luckGrade).name,
                                rarityColor: getItemRarity(drop.itemId, drop.luckGrade).color,
                                luckGrade: drop.luckGrade
                            });
                        });
                        return;
                    }
                    items.forEach(drop => {
                        const itemShareInGroup = (drop.dropWeight ?? 0) / totalWeightInGroup;
                        const finalItemChance = groupChancePerRoll * itemShareInGroup;
                        displayableItemsForThisCheck.push({
                            itemName: drop.itemName, itemId: drop.itemId, artifactName: drop.artifactName,
                            itemCount: drop.itemCount || 1, finalCalculatedChance: finalItemChance,
                            rarityName: getItemRarity(drop.itemId, drop.luckGrade).name,
                            rarityColor: getItemRarity(drop.itemId, drop.luckGrade).color,
                            luckGrade: drop.luckGrade
                        });
                        P_any_item_one_roll_from_check += finalItemChance;
                    });
                });
                P_any_item_one_roll_from_check = Math.min(1, P_any_item_one_roll_from_check);
                const chance_check_yields_any_item_N_rolls = (P_any_item_one_roll_from_check > 0 && N_for_this_check > 0)
                    ? (1 - Math.pow(1 - P_any_item_one_roll_from_check, N_for_this_check)) * 100 : 0;
                if (displayableItemsForThisCheck.length > 0) {
                    const sortedItems = [...displayableItemsForThisCheck].sort((a,b) => {
                        const rA = getRarityRank(a.rarityName); const rB = getRarityRank(b.rarityName);
                        if (rA !== rB) return rA - rB;
                        return b.finalCalculatedChance - a.finalCalculatedChance;
                    });
                    allParsableDropCheckObjects.push({
                        tabGroupingKey: allowedPoolId_originalCase, checkIndex: checkIndex,
                        checkType: checkData.checkType || 'Unknown Pool Type',
                        N_rolls_for_this_check: N_for_this_check,
                        chance_check_yields_any_item_N_rolls: chance_check_yields_any_item_N_rolls,
                        displayableItems: sortedItems,
                        firstItemRarityRank: sortedItems.length > 0 ? getRarityRank(sortedItems[0].rarityName) : Infinity,
                    });
                }
            });
        });
    } else {
        if (!lootDataForGrade && poolsToIterate && poolsToIterate.length > 0) {
            logicUsed = 'AllowedPools_Present_But_No_LootDataForGrade_V3.1';
        } else if (!canProcessAllowedPools) {
            logicUsed = 'NoAllowedPools_Or_Invalid_V3.1';
        }
    }

    let poolsAfterSearchApplied = [...allParsableDropCheckObjects];
    let searchWasPerformedAndYieldedNoItemsGlobally = false;
    if (searchTermParam && searchTermParam.trim() !== '') {
        const lowerSearchTerm = searchTermParam.toLowerCase().trim();
        poolsAfterSearchApplied = poolsAfterSearchApplied.map(poolData => {
            const filteredItems = poolData.displayableItems.filter(item => {
                const itemName = (item.itemName || '').toLowerCase();
                const artifactName = (item.artifactName || '').toLowerCase();
                const cleanedItemIdDisplay = cleanItemIdForDisplay(item.itemId).toLowerCase();
                return itemName.includes(lowerSearchTerm) || artifactName.includes(lowerSearchTerm) || cleanedItemIdDisplay.includes(lowerSearchTerm);
            });
            return { ...poolData, displayableItems: filteredItems };
        });
        if (allParsableDropCheckObjects.some(pd => pd.displayableItems.length > 0) &&
            poolsAfterSearchApplied.every(pd => pd.displayableItems.length === 0)) {
            searchWasPerformedAndYieldedNoItemsGlobally = true;
        }
    }


    if (canProcessAllowedPools && lootDataForGrade) {
        const sortedAllowedPools = [...new Set(poolsToIterate)].sort((keyA, keyB) => {
            const entryA = findLootGroupEntryCaseInsensitive(lootDataForGrade, keyA);
            const entryB = findLootGroupEntryCaseInsensitive(lootDataForGrade, keyB);
            let labelA = (entryA && entryA.entityName) ? entryA.entityName : keyA;
            let labelB = (entryB && entryB.entityName) ? entryB.entityName : keyB;
            if (labelA.toLowerCase().startsWith("drop_")) labelA = labelA.substring(5).trim();
            if (labelB.toLowerCase().startsWith("drop_")) labelB = labelB.substring(5).trim();
            return formatDisplayName(cleanItemIdForDisplay(labelA)).localeCompare(formatDisplayName(cleanItemIdForDisplay(labelB)));
        });

        sortedAllowedPools.forEach(allowedPoolId_originalCase => {
            const lootGroupEntryForTabDisplay = findLootGroupEntryCaseInsensitive(lootDataForGrade, allowedPoolId_originalCase);
            let baseStringForTabLabel = (lootGroupEntryForTabDisplay?.entityName?.trim()) || allowedPoolId_originalCase;
            if (baseStringForTabLabel.toLowerCase().startsWith("drop_")) {
                baseStringForTabLabel = baseStringForTabLabel.substring("drop_".length).trim();
            }
            const mainTabLabel = formatDisplayName(cleanItemIdForDisplay(baseStringForTabLabel));

            const dropChecksInThisTab_PreSearch = allParsableDropCheckObjects.filter(
                pd => pd.tabGroupingKey === allowedPoolId_originalCase
            );
            const dropChecksInThisTab_PostSearchAndLuck = poolsAfterSearchApplied.filter(
                pd => pd.tabGroupingKey === allowedPoolId_originalCase && pd.displayableItems.length > 0
            );

            if (dropChecksInThisTab_PostSearchAndLuck.length > 0) {
                const poolsByCheckTypeInTab = new Map();
                dropChecksInThisTab_PostSearchAndLuck.forEach(poolData => {
                    const checkTypeKey = poolData.checkType || 'Unknown Pool Type';
                    if (!poolsByCheckTypeInTab.has(checkTypeKey)) poolsByCheckTypeInTab.set(checkTypeKey, []);
                    poolsByCheckTypeInTab.get(checkTypeKey).push(poolData);
                });

                const sortedCheckTypeEntries = Array.from(poolsByCheckTypeInTab.entries()).sort(([ctA], [ctB]) => {
                    const orderA = (POOL_CATEGORIES[getPoolCategoryKey(ctA)] || POOL_CATEGORIES.OTHER)?.order ?? 99;
                    const orderB = (POOL_CATEGORIES[getPoolCategoryKey(ctB)] || POOL_CATEGORIES.OTHER)?.order ?? 99;
                    if (orderA !== orderB) return orderA - orderB;
                    return formatCheckTypeForDisplay(ctA).localeCompare(formatCheckTypeForDisplay(ctB));
                });

                let categorizedPoolsForMainTabHtml = [];
                let actualPoolsWithItemsInTab = 0;
                sortedCheckTypeEntries.forEach(([checkType, poolsForThisCheckType]) => {
                    let htmlContentForThisCheckTypeSubSection = '';
                    const sortedPoolsInCheckType = [...poolsForThisCheckType].sort((a, b) => {
                        if (a.firstItemRarityRank !== b.firstItemRarityRank) return a.firstItemRarityRank - b.firstItemRarityRank;
                        return a.checkIndex - b.checkIndex;
                    });
                    sortedPoolsInCheckType.forEach(poolData => {
                        htmlContentForThisCheckTypeSubSection += generateHtmlForDropCheckPool(poolData, true, true);
                        actualPoolsWithItemsInTab++;
                    });
                    if (htmlContentForThisCheckTypeSubSection.trim() !== '') {
                         categorizedPoolsForMainTabHtml.push({
                            categoryLabel: formatCheckTypeForDisplay(checkType),
                            poolsHtmlForCategory: htmlContentForThisCheckTypeSubSection,
                        });
                    }
                });

                if (actualPoolsWithItemsInTab > 0) {
                    lootDataOutput.tabs.push({
                        key: allowedPoolId_originalCase,
                        tabLabel: escapeHTML(mainTabLabel),
                        categorizedPools: categorizedPoolsForMainTabHtml,
                        poolCount: actualPoolsWithItemsInTab,
                        tabSpecificNoItemsMessage: '',
                    });
                }
            } else if (searchTermParam && searchTermParam.trim() !== '') {
                const itemsWerePresentInThisTab_PreSearch = dropChecksInThisTab_PreSearch.some(pd => pd.displayableItems.length > 0);
                if (itemsWerePresentInThisTab_PreSearch) {
                    lootDataOutput.tabs.push({
                        key: allowedPoolId_originalCase,
                        tabLabel: escapeHTML(mainTabLabel),
                        categorizedPools: [],
                        poolCount: 0,
                        tabSpecificNoItemsMessage: `<div class="panel-info-message"><i>No items in this pool match your search: "${escapeHTML(searchTermParam)}".</i></div>`,
                    });
                }

            }

        });
    }

    const trulyDisplayableTabs = lootDataOutput.tabs.filter(t => t.poolCount > 0);

    if (trulyDisplayableTabs.length === 0) {
        let message = '';
        const gameModeObjectVal = effectiveGameModes && gameModeKeyParam && Object.values(effectiveGameModes).find(m => m.key === gameModeKeyParam);
        const modeLabel = gameModeObjectVal ? gameModeObjectVal.label : '';
        const titleSuffix = modeLabel ? ` (${modeLabel} - Grade ${dungeonGradeKeyParam})` : ` (Grade ${dungeonGradeKeyParam})`;

        if (lootDataOutput.tabs.length > 0 && lootDataOutput.tabs.every(t => t.poolCount === 0 && t.tabSpecificNoItemsMessage)) {
          // Keep er happy
        } else if (searchWasPerformedAndYieldedNoItemsGlobally) {
            message = `No loot items match your search: "${escapeHTML(searchTermParam)}" for this object.`;
        } else if (!lootDataForGrade && poolsToIterate && poolsToIterate.length > 0) {
            message = `Loot data for the current grade (${dungeonGradeKeyParam}) is unavailable. Cannot display drops from configured pools. (Logic: ${logicUsed})`;
        } else if (canProcessAllowedPools) {
            if (!anyLootConfiguredOverall && poolsToIterate.length > 0) {
                message = `The configured loot pools (via 'allowed_pools') yielded no processable items for grade ${dungeonGradeKeyParam}${titleSuffix}. (Logic: ${logicUsed})`;
            } else if (anyLootConfiguredOverall && allParsableDropCheckObjects.every(p => p.displayableItems.length === 0)) {
                message = `All loot from configured pools was filtered out by criteria (e.g., luck, rarity) for grade ${dungeonGradeKeyParam}${titleSuffix}. (Logic: ${logicUsed})`;
            } else {
                 message = `No displayable loot found for this object using 'allowed_pools' configuration for grade ${dungeonGradeKeyParam}${titleSuffix}. (Logic: ${logicUsed})`;
            }
        } else {
             message = `No 'allowed_pools' configured for this object. Loot display requires 'allowed_pools' to be defined for grade ${dungeonGradeKeyParam}${titleSuffix}. (Logic: ${logicUsed})`;
        }

        if (message && !lootDataOutput.noLootMessageHtmlIfAny) {
             lootDataOutput.noLootMessageHtmlIfAny = `<div class="panel-info-message"><i>${message}</i></div>`;
        }
    }

    const hasDisplayableItemContent = trulyDisplayableTabs.length > 0;
    const hasSearchMessageTabs = lootDataOutput.tabs.length > 0 && lootDataOutput.tabs.some(t => t.poolCount === 0 && t.tabSpecificNoItemsMessage);
    const hasAnyContentForPopup = hasDisplayableItemContent || hasSearchMessageTabs || lootDataOutput.noLootMessageHtmlIfAny;

    const separatorHtml = (hasDisplayableItemContent || hasSearchMessageTabs) ? `<hr class="panel-section-separator">` : '';

    if (!hasAnyContentForPopup) {
        const gradeSuffix = dungeonGradeKeyParam ? ` for grade ${dungeonGradeKeyParam}` : '';
        lootDataOutput.noLootMessageHtmlIfAny = `<div class="panel-info-message"><i>No information available for this object${gradeSuffix}. (Logic: ${logicUsed})</i></div>`;
    }

    const finalOutput = { mainHeaderHtml, spawnChancesHtml: '', separatorHtml, lootData: lootDataOutput };
    return finalOutput;
}

function formatCheckTypeForDisplay(checkType) {
    if (!checkType || typeof checkType !== 'string' || checkType.trim() === '' || checkType === 'Unknown Pool Type') {
        return (typeof POOL_CATEGORIES !== 'undefined' && POOL_CATEGORIES.OTHER) ? POOL_CATEGORIES.OTHER.label : 'Other Loot';
    }
    const trimmedCheckType = checkType.trim();
    if (trimmedCheckType.includes(' ')) return trimmedCheckType;
    let result = trimmedCheckType.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/([A-Z])([A-Z][a-z])/g, '$1 $2');
    return result.charAt(0).toUpperCase() + result.slice(1);
}

export function initializeCollapsiblePools(popupElement) {
    if (!popupElement) return;

    const triggers = popupElement.querySelectorAll('.collapsible-trigger');
    triggers.forEach(trigger => {
        if (trigger.dataset.collapsibleClickListenerAttached === 'true') return;
        trigger.dataset.collapsibleClickListenerAttached = 'true';

        trigger.addEventListener('click', (event) => {
            event.stopPropagation();
            const content = trigger.nextElementSibling;
            const icon = trigger.querySelector('.pool-toggle-icon');
            if (content && content.classList.contains('pool-content')) {
                const isCollapsed = content.style.display === 'none' || content.style.display === '';
                content.style.display = isCollapsed ? 'block' : 'none';
                if (icon) icon.textContent = isCollapsed ? '▼' : '►';
            }
        });
    });
}

function canPlace(occupiedGrid, r, c, rowSpan, colSpan, maxRDim, maxCDim) {
    if (r < 0 || c < 0 || r + rowSpan > maxRDim || c + colSpan > maxCDim) return false;
    for (let i = 0; i < rowSpan; i++) {
        for (let j = 0; j < colSpan; j++) {
            if (r + i >= maxRDim || c + j >= maxCDim || occupiedGrid[r + i][c + j]) {
                return false;
            }
        }
    }
    return true;
}

export function calculateModuleOverlaysAndBounds(currentMapData, selectedMapType) {
    const gridOverlays = [];
    const orderedModuleNames = [];
    let finalGridBounds = null;
    const defaultBounds = L.latLngBounds([[0, 0], [CELL_HEIGHT, CELL_WIDTH]]);

    if (!currentMapData || typeof currentMapData !== 'object' || Object.keys(currentMapData).length === 0) {
        return { overlays: [], gridBounds: defaultBounds, orderedModuleNames: [] };
    }

    const isFixedLayout = selectedMapType === 'IceCave' || selectedMapType === 'IceAbyss';
    const fixedLayoutDefinition = selectedMapType === 'IceCave' ? ICE_CAVE_LAYOUT : (selectedMapType === 'IceAbyss' ? ICE_ABYSS_LAYOUT : null);

    if (isFixedLayout && fixedLayoutDefinition) {
        let minLatOverall = Infinity, minLngOverall = Infinity, maxLatOverall = -Infinity, maxLngOverall = -Infinity;
        let placedCount = 0;
        fixedLayoutDefinition.forEach(layoutItem => {
            const { moduleName, row, col } = layoutItem;
            const moduleContent = currentMapData[moduleName];
            if (!moduleContent || !moduleContent.imagePath || typeof moduleContent.imagePath !== 'string' || !moduleContent.dimensions) {
                return;
            }
            const moduleDisplayName = (moduleContent.Module_LocalizedString && moduleContent.Module_LocalizedString !== "N/A") ? moduleContent.Module_LocalizedString : cleanModuleNameForDisplayFallback(moduleName);
            const tileDimensions = moduleContent.dimensions;
            const colSpan = Math.max(1, Math.round(tileDimensions.width / BASE_MODULE_SLOT_WIDTH));
            const rowSpan = Math.max(1, Math.round(tileDimensions.height / BASE_MODULE_SLOT_HEIGHT));

            const moduleVisualWidth = (colSpan * CELL_WIDTH) + ((colSpan - 1) * GRID_PADDING);
            const moduleVisualHeight = (rowSpan * CELL_HEIGHT) + ((rowSpan - 1) * GRID_PADDING);

            const minLng = col * (CELL_WIDTH + GRID_PADDING);
            const minLat = row * (CELL_HEIGHT + GRID_PADDING);
            const maxLng = minLng + moduleVisualWidth;
            const maxLat = minLat + moduleVisualHeight;

            gridOverlays.push({ url: moduleContent.imagePath, bounds: [[minLat, minLng], [maxLat, maxLng]], moduleName: moduleName, moduleDisplayName: moduleDisplayName });
            orderedModuleNames.push(moduleName);
            placedCount++;

            minLatOverall = Math.min(minLatOverall, minLat);
            minLngOverall = Math.min(minLngOverall, minLng);
            maxLatOverall = Math.max(maxLatOverall, maxLat);
            maxLngOverall = Math.max(maxLngOverall, maxLng);
        });
        if (placedCount > 0) finalGridBounds = L.latLngBounds([[minLatOverall, minLngOverall], [maxLatOverall, maxLngOverall]]);
        else finalGridBounds = defaultBounds;
    } else {
        const displayableModuleEntries = Object.entries(currentMapData)
            .filter(([, mc]) => mc && mc.imagePath && typeof mc.imagePath === 'string' && mc.dimensions)
            .sort(([a], [b]) => a.localeCompare(b));

        if (displayableModuleEntries.length === 0) {
            return { overlays: [], gridBounds: defaultBounds, orderedModuleNames: [] };
        }

        let totalBaseSlotArea = 0;
        let maxModuleColSpan = 1;
        let modulesWithSpan = displayableModuleEntries.map(([moduleName, moduleContent]) => {
            const td = moduleContent.dimensions;
            const colSpan = Math.max(1, Math.round(td.width / BASE_MODULE_SLOT_WIDTH));
            const rowSpan = Math.max(1, Math.round(td.height / BASE_MODULE_SLOT_HEIGHT));
            totalBaseSlotArea += colSpan * rowSpan;
            if (colSpan > maxModuleColSpan) maxModuleColSpan = colSpan;
            const mdn = (moduleContent.Module_LocalizedString && moduleContent.Module_LocalizedString !== "N/A") ? moduleContent.Module_LocalizedString : cleanModuleNameForDisplayFallback(moduleName);
            return { moduleName, moduleContent, colSpan, rowSpan, moduleDisplayName: mdn, area: colSpan * rowSpan };
        });

        modulesWithSpan.sort((a, b) => {
            if (b.area !== a.area) {
                return b.area - a.area;
            }
            return a.moduleName.localeCompare(b.moduleName);
        });

        let targetGridWidthSlots = Math.max(1, Math.ceil(Math.sqrt(totalBaseSlotArea)));
        const SCAN_MAX_COLS = Math.max(targetGridWidthSlots, maxModuleColSpan);
        const MAX_GRID_ROWS = Math.max(10, Math.ceil(totalBaseSlotArea / targetGridWidthSlots) + Math.ceil(modulesWithSpan.length / SCAN_MAX_COLS) + 5);

        const occupiedGrid = Array(MAX_GRID_ROWS).fill(null).map(() => Array(SCAN_MAX_COLS).fill(false));
        const placedModulesInfo = [];

        modulesWithSpan.forEach((moduleData) => {
            const { moduleName, colSpan, rowSpan } = moduleData;
            let placed = false;
            for (let r = 0; r < MAX_GRID_ROWS; r++) {
                for (let c = 0; c < SCAN_MAX_COLS; c++) {
                    if (c + colSpan > SCAN_MAX_COLS) break;

                    if (canPlace(occupiedGrid, r, c, rowSpan, colSpan, MAX_GRID_ROWS, SCAN_MAX_COLS)) {
                        const mvw = (colSpan * CELL_WIDTH) + ((colSpan - 1) * GRID_PADDING);
                        const mvh = (rowSpan * CELL_HEIGHT) + ((rowSpan - 1) * GRID_PADDING);
                        const minLng = c * (CELL_WIDTH + GRID_PADDING);
                        const minLat = r * (CELL_HEIGHT + GRID_PADDING);
                        const maxLng = minLng + mvw;
                        const maxLat = minLat + mvh;

                        gridOverlays.push({
                            url: moduleData.moduleContent.imagePath,
                            bounds: [[minLat, minLng], [maxLat, maxLng]],
                            moduleName,
                            moduleDisplayName: moduleData.moduleDisplayName
                        });

                        for (let i = 0; i < rowSpan; i++) {
                            for (let j = 0; j < colSpan; j++) {
                                if ((r + i < MAX_GRID_ROWS) && (c + j < SCAN_MAX_COLS)) {
                                    occupiedGrid[r + i][c + j] = true;
                                }
                            }
                        }
                        placedModulesInfo.push({ moduleName, r, c });
                        placed = true;
                        break;
                    }
                }
                if (placed) break;
            }
            if (!placed) console.error(`DYNAMIC LAYOUT FAILURE: Module ${moduleName} (spans ${rowSpan}x${colSpan}, area ${moduleData.area}) could not be placed in ${MAX_GRID_ROWS}x${SCAN_MAX_COLS} grid.`);
        });

        placedModulesInfo.sort((a,b) => {
            if (a.r !== b.r) return a.r - b.r;
            return a.c - b.c;
        });
        placedModulesInfo.forEach(info => orderedModuleNames.push(info.moduleName));

        if (gridOverlays.length > 0) {
            let minLatO = Infinity, minLngO = Infinity, maxLatO = -Infinity, maxLngO = -Infinity;
            gridOverlays.forEach(ov => {
                minLatO = Math.min(minLatO, ov.bounds[0][0]);
                minLngO = Math.min(minLngO, ov.bounds[0][1]);
                maxLatO = Math.max(maxLatO, ov.bounds[1][0]);
                maxLngO = Math.max(maxLngO, ov.bounds[1][1]);
            });
            finalGridBounds = L.latLngBounds([[minLatO, minLngO], [maxLatO, maxLngO]]);
        } else {
            finalGridBounds = defaultBounds;
        }
    }
    return { overlays: gridOverlays, gridBounds: finalGridBounds, orderedModuleNames };
}

function addEntryToUncategorized(iconStates, entry) {
    if (!iconStates['Uncategorized']) {
        iconStates['Uncategorized'] = [];
    }
    iconStates['Uncategorized'].push(entry);
}

export function calculateMarkersAndIconStates(currentMapDataWithActors, mapOverlays, selectedMapType, gameMode, mapIconScale, categoryPatterns) {
    const newIconStates = initializeIconsStructure(categoryPatterns);
    const gridMarkers = [];

    if (!currentMapDataWithActors || typeof currentMapDataWithActors !== 'object' || Object.keys(currentMapDataWithActors).length === 0) {
        return { markers: [], iconStates: newIconStates };
    }
    if (!mapOverlays || mapOverlays.length === 0) {
        return { markers: [], iconStates: newIconStates };
    }

    const moduleBoundsMap = new Map();
    const moduleDisplayNameMap = new Map();
    mapOverlays.forEach(ov => {
        moduleBoundsMap.set(ov.moduleName, ov.bounds);
        moduleDisplayNameMap.set(ov.moduleName, ov.moduleDisplayName);
    });

    Object.entries(currentMapDataWithActors).forEach(([moduleName, moduleContent]) => {
        if (moduleContent?.data && Array.isArray(moduleContent.data) && moduleContent.data.length > 0 && moduleBoundsMap.has(moduleName)) {
            const moduleGridBounds = moduleBoundsMap.get(moduleName);

            const tileDimensionsForLayout = moduleContent.dimensions;
            const currentModuleDisplayName = moduleDisplayNameMap.get(moduleName);

            if (!moduleGridBounds || !tileDimensionsForLayout) {
                return;
            }

            moduleContent.data.forEach((actorData, actorIndex) => {
                if (!actorData?.object_name || !actorData?.object_location) return;

                const moduleInfoForMarker = {
                    moduleName,
                    moduleDisplayName: currentModuleDisplayName,
                    gridBounds: moduleGridBounds,
                    dimensions: tileDimensionsForLayout,
                    originalMetaDimensions: moduleContent.originalMetaDimensions,
                    moduleTextureActualDimensions: moduleContent.moduleTextureActualDimensions
                };
                const marker = createMarkerObject(moduleInfoForMarker, actorData, actorIndex, selectedMapType, gameMode, mapIconScale, actorData.LocalizedString);

                if (marker) {
                    gridMarkers.push(marker);
                    const { category, subcategory } = getCategoryAndSubcategory(actorData.object_name, actorData.type, categoryPatterns);
                    const entryForIconStates = { ...actorData, marker: marker };
                    try {
                        const targetCat = newIconStates[category];
                        if (category && subcategory && targetCat?.[subcategory] && Array.isArray(targetCat[subcategory])) {
                             targetCat[subcategory].push(entryForIconStates);
                        } else if (category && !subcategory && Array.isArray(targetCat)) {
                             targetCat.push(entryForIconStates);
                        } else if (category && !subcategory && targetCat && typeof targetCat === 'object' && !Array.isArray(targetCat)) {
                            const defaultSub = Object.keys(targetCat)[0];
                            if(defaultSub && targetCat[defaultSub] && Array.isArray(targetCat[defaultSub])) {
                                targetCat[defaultSub].push(entryForIconStates);
                            } else {
                                addEntryToUncategorized(newIconStates, entryForIconStates);
                            }
                        } else {
                             addEntryToUncategorized(newIconStates, entryForIconStates);
                        }
                    } catch (e) {
                        addEntryToUncategorized(newIconStates, entryForIconStates);
                    }
                }
            });
        }
    });
    return { markers: gridMarkers, iconStates: newIconStates };
}

export function generateModuleNameSvg(moduleDisplayName, originalModuleName, options = {}) {
    const config = {
        baseFontSize: 12, fontFamily: "'Inter', 'Arial', sans-serif", fontWeight: '500', letterSpacing: '0.3px', textColor: "#F0F0F0",
        strokeColor: "rgba(0,0,0,0.7)", strokeWidth: 0.7, padding: 8, backgroundColor: "rgba(40, 45, 55, 0.88)", borderRadius: 5,
        minWidth: 40, maxWidth: 170, ellipsis: '…', textTransform: 'none', formatDisplayNameMaxLength: 400, ...options,
    };
    const svgTitleText = escapeHTML(originalModuleName);
    let processedModuleNameForDisplay = (moduleDisplayName && moduleDisplayName !== "N/A") ? moduleDisplayName : cleanModuleNameForDisplayFallback(originalModuleName);
    const sanitizedModuleNameForDisplay = escapeHTML(processedModuleNameForDisplay);
    let displayText = formatDisplayName(sanitizedModuleNameForDisplay, config.formatDisplayNameMaxLength);
    if (config.textTransform === 'uppercase') displayText = displayText.toUpperCase();
    else if (config.textTransform === 'capitalize' && config.textTransform !== 'none') displayText = displayText.replace(/\b\w/g, char => char.toUpperCase());

    const ESTIMATED_CHAR_WIDTH_FACTOR = 0.6;
    let textIntrinsicWidth = displayText.length * config.baseFontSize * ESTIMATED_CHAR_WIDTH_FACTOR;
    const availableWidthForText = config.maxWidth - (2 * config.padding);

    if (textIntrinsicWidth > availableWidthForText && displayText.length > 0) {
        const ellipsisWidth = config.ellipsis.length * config.baseFontSize * ESTIMATED_CHAR_WIDTH_FACTOR;
        let truncatedText = "";
        if (availableWidthForText > ellipsisWidth) {
            for (let i = 0; i < displayText.length; i++) {
                const char = displayText[i];
                const estimatedNewWidth = (truncatedText.length + 1) * config.baseFontSize * ESTIMATED_CHAR_WIDTH_FACTOR + ellipsisWidth;
                if (estimatedNewWidth <= availableWidthForText) {
                    truncatedText += char;
                } else {
                    break;
                }
            }
            displayText = truncatedText.trimEnd() + config.ellipsis;
        } else {
             displayText = availableWidthForText >= ellipsisWidth ? config.ellipsis : "";
        }
        textIntrinsicWidth = displayText.length * config.baseFontSize * ESTIMATED_CHAR_WIDTH_FACTOR;
    }

    const textHeight = config.baseFontSize;
    const contentWidth = Math.max(config.minWidth - (2 * config.padding), textIntrinsicWidth);
    const svgWidth = Math.min(config.maxWidth, Math.max(config.minWidth, contentWidth + (2 * config.padding)));
    const svgHeight = textHeight + (2 * config.padding);
    const textX = svgWidth / 2;
    const textY = svgHeight / 2;

    let backgroundElement = '';
    if (config.backgroundColor && config.backgroundColor !== 'transparent') {
        backgroundElement = `<rect x="0" y="0" width="${svgWidth}" height="${svgHeight}" fill="${config.backgroundColor}" rx="${config.borderRadius}" ry="${config.borderRadius}"/>`;
    }
    let textStrokeAttributes = '';
    if (config.strokeWidth > 0 && config.strokeColor && config.strokeColor !== 'transparent') {
        textStrokeAttributes = `stroke="${config.strokeColor}" stroke-width="${config.strokeWidth}px" stroke-linejoin="round"`;
    }

    const finalStyleTransform = (config.textTransform === 'none' || config.textTransform === 'capitalize') ? 'none' : config.textTransform;

    const svgString = `<svg xmlns="http://www.w3.org/2000/svg" width="${svgWidth}" height="${svgHeight}" viewBox="0 0 ${svgWidth} ${svgHeight}"><title>${svgTitleText}</title>${backgroundElement}<text x="${textX}" y="${textY}" font-family="${config.fontFamily}" font-size="${config.baseFontSize}px" font-weight="${config.fontWeight}" fill="${config.textColor}" letter-spacing="${config.letterSpacing}" ${textStrokeAttributes} text-anchor="middle" dominant-baseline="middle" style="text-transform: ${finalStyleTransform}; paint-order: stroke;">${displayText}</text></svg>`;
    return svgString.replace(/\s\s+/g, ' ').replace(/>\s+</g, '><').trim();
}